<script>
  import { onDestroy, onMount } from 'svelte';
  import HiddenField from "./subscriptions/hiddenField.svelte";
  import { openRebuyCart } from './rebuyCart.js';
  import {
    roast, roastDisplayValue,
    processingFilters, flavorFilters, descriptorFilters,
    size, sizeDisplayValue,
    shippingFrequency, shippingFrequencyDisplayValue,
  } from "./subscriptions/stores.js";

  export let debug;
  export let recurring;

  let PRICE_300G   = 24.00;
  let PRICE_1KILO  = 62.50;
  const DISCOUNT_PCT = 0.05;
  // Subscribe & Save kicks in at this many bags per shipment; below it, full price.
  const MIN_DISCOUNT_BAGS = 2;
  const DISCOUNT_LABEL = `${Math.round(DISCOUNT_PCT * 100)}% off`;
  const SHIPPING_FLAT_RATE = 6.95;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  });

  let bundleData = (typeof window !== 'undefined' && window.coavaBundle)
    ? { ...window.coavaBundle }
    : { variantId: null, productHandle: 'coffee-subscription-plan-1', sellingPlans: [] };
  const coffeeProducts = (typeof window !== 'undefined' && window.coavaCoffees) ? window.coavaCoffees : [];
  const addonProducts  = (typeof window !== 'undefined' && window.coavaAddons)   ? window.coavaAddons   : [];

  // Derive base 300g and 1kg prices from subscription coffee variants
  for (const p of coffeeProducts) {
    for (const v of p.variants || []) {
      const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
      if (opts.includes('300') && v.price > 0) { PRICE_300G = v.price / 100; break; }
    }
    for (const v of p.variants || []) {
      const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
      if (opts.includes('kilo') && v.price > 0) { PRICE_1KILO = v.price / 100; break; }
    }
    if (PRICE_300G !== 24.00 && PRICE_1KILO !== 62.50) break;
  }

  function fmtPrice(n) {
    const s = n.toFixed(2);
    return '$' + (s.endsWith('.00') ? s.slice(0, -3) : s);
  }

  function getProductImage(product, sizeLabel) {
    if (!product || !product.variants || !product.variants.length) return product && product.image;
    const sizeKey = (sizeLabel || '').toLowerCase().includes('kilo') ? 'kilo' : '300';
    const match = product.variants.find(v => {
      const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
      return opts.includes(sizeKey) && v.image;
    });
    return (match && match.image) || product.image;
  }

  function getVariantPrice(product, sizeLabel) {
    if (!product || !product.variants || !product.variants.length) {
      return (sizeLabel || '').toLowerCase().includes('kilo') ? PRICE_1KILO : PRICE_300G;
    }
    const sizeKey = (sizeLabel || '').toLowerCase().includes('kilo') ? 'kilo' : '300';
    const match = product.variants.find(v => {
      const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
      return opts.includes(sizeKey) && v.price > 0;
    });
    if (match) return match.price / 100;
    return (sizeLabel || '').toLowerCase().includes('kilo') ? PRICE_1KILO : PRICE_300G;
  }

  let submitting = false;
  let submitError = '';
  let price = null;
  let priceNum = null;
  let perBagPrice = null;
  let discountApplied = false;
  let grandTotal = null;
  let originalPrice = null;

  // Step 2: multi-coffee selection by quantity.
  // coffeeQty maps a product id (string) -> number of bags chosen for it.
  // The total must equal the bag count picked in step 1.
  const CURATED = { id: 'curated', title: 'Curated by our team', handle: null, variants: [], tags: [] };
  let coffeeQty    = {}; // keys: 'productId:Roast' (e.g. '12345:Drip') or 'curated'
  let curatedRoasts = []; // ['Drip'] | ['Espresso'] | ['Drip','Espresso']

  // Step 3: add-ons — an array so the customer can add as many as they like.
  // Each: { id: 'productId:Roast', productId, product, size: '300g' | '1 Kilo', qty, roast }
  // Keyed by product AND roast (like step 2's coffeeQty) so Drip and Espresso of the same
  // coffee are two independent add-on lines, each picked from the card itself.
  let addonItems = [];
  // Step 3 roast filter — mirrors step 2's Roast segment but scoped to the add-on grid,
  // so it never disturbs the subscription roast in $roast.
  let addonRoastFilter = null;

  let currentStep = 1;
  let sbContainer;
  // Persisted: distinguishes "never added yet" from "added, then emptied elsewhere".
  let hasAddedToCart = false;

  function lookupProduct(id) {
    if (id === 'curated') return CURATED;
    return coffeeProducts.find(p => String(p.id) === String(id)) || null;
  }

  // Add-ons come from the separate 'addon-coffees' collection (window.coavaAddons), so
  // they aren't in coffeeProducts. Anything resolving a cart line or a saved add-on has
  // to search both lists.
  function lookupAnyProduct(id) {
    return lookupProduct(id)
      || addonProducts.find(p => String(p.id) === String(id))
      || null;
  }

  // ── Shared availability rules (steps 2 and 3) ──
  /**
   * Is `roastVal` actually orderable for this product? Distinct from roastCompatible(),
   * which is a filter test: this is a hard rule — a Drip-only coffee must never be
   * ordered as Espresso. Products carrying no roast options at all (non-coffee add-ons)
   * are orderable only with a null roast.
   */
  function roastAvailable(product, roastVal) {
    if (!product) return false;
    if (!roastVal) return !product.hasDrip && !product.hasEspresso;
    if (roastVal === 'Drip')     return !!product.hasDrip;
    if (roastVal === 'Espresso') return !!product.hasEspresso;
    return false;
  }

  /**
   * Does a real, in-stock variant exist for this size? getVariantPrice() and
   * getCoffeeVariantId() both fall back when they can't match (a flat price, and
   * variants[0] respectively), which is fine for display but useless for validation —
   * an unmatched 1 Kilo would show $62.50 and then quietly order the 300g variant.
   */
  function variantSizeMatches(optsLower, sizeLabel) {
    if (!(sizeLabel || '').toLowerCase().includes('kilo')) return optsLower.includes('300');
    // A 2 Kilo / 3 Kilo variant must not satisfy a 1 Kilo request.
    if (/(^|[^\d])[2-9]\s*kilo/.test(optsLower)) return false;
    return optsLower.includes('kilo');
  }

  function sizeVariantAvailable(product, roastVal, sizeLabel) {
    return ((product && product.variants) || []).some(v => {
      if (v.available === false) return false;
      const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
      if (!variantSizeMatches(opts, sizeLabel)) return false;
      return roastVal ? opts.includes(roastVal.toLowerCase()) : true;
    });
  }

  // Prefer the bag size chosen in step 1; fall back to the other size when this coffee
  // doesn't sell it. null means nothing is orderable for this roast at all.
  function resolveAddonSize(product, roastVal, preferred) {
    if (sizeVariantAvailable(product, roastVal, preferred)) return preferred;
    const other = (preferred || '').toLowerCase().includes('kilo') ? '300g' : '1 Kilo';
    return sizeVariantAvailable(product, roastVal, other) ? other : null;
  }

  // `preferred` is passed in from the markup rather than read off bagSizeLabel here, so
  // Svelte re-evaluates the card steppers when the step-1 bag size changes.
  function addonOrderable(product, roastVal, preferred) {
    return roastAvailable(product, roastVal)
        && !!resolveAddonSize(product, roastVal, preferred);
  }

  // ── Add-on identity: one line per product + roast ──
  // Products with no roast options at all (non-coffee add-ons) collapse to a single
  // roast-less key.
  function addonKey(productId, roastVal) {
    return `${productId}:${roastVal || ''}`;
  }

  /**
   * Collapse duplicates by key, summing quantities. The {#each} over addonItems is keyed
   * by item.id, so two entries sharing a key would be a runtime error — restore and cart
   * sync both build their lists from untrusted data, so they merge through here first.
   */
  function mergeAddonItems(list) {
    const merged = [];
    for (const it of list) {
      const existing = merged.find(m => m.id === it.id);
      if (existing) existing.qty += it.qty;
      else merged.push({ ...it });
    }
    return merged;
  }

  // ── Persistence: keep the full selection in localStorage until checkout / cache clear ──
  const STORAGE_KEY = 'coavaSubBuilder';
  let restored = false;            // becomes true after we load any saved selection
  let subscriptionInCart = false;  // true if the cart already holds a subscription bundle

  function restoreSelection() {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved) {
        if (saved.roast)       { roast.set(saved.roast); roastDisplayValue.set(saved.roastDisplay || saved.roast); }
        if (saved.size)        size.set(saved.size);
        if (saved.sizeDisplay) sizeDisplayValue.set(saved.sizeDisplay);
        if (Array.isArray(saved.freq))       shippingFrequency.set(saved.freq);
        if (saved.freqDisplay) shippingFrequencyDisplayValue.set(saved.freqDisplay);
        if (Array.isArray(saved.processing)) processingFilters.set(saved.processing);
        if (Array.isArray(saved.flavor))     flavorFilters.set(saved.flavor);
        if (Array.isArray(saved.descriptor)) descriptorFilters.set(saved.descriptor);
        if (saved.coffeeQty && typeof saved.coffeeQty === 'object') {
          const clean = {};
          for (const [key, q] of Object.entries(saved.coffeeQty)) {
            if (q <= 0) continue;
            if (key === 'curated') { clean['curated'] = q; continue; }
            const colonIdx = key.lastIndexOf(':');
            if (colonIdx !== -1) {
              if (lookupProduct(key.substring(0, colonIdx))) clean[key] = q;
            } else if (lookupProduct(key)) {
              clean[key + ':Drip'] = q; // legacy: default to Drip
            }
          }
          coffeeQty = clean;
        }
        if (saved.curatedRoasts && Array.isArray(saved.curatedRoasts)) {
          curatedRoasts = saved.curatedRoasts;
        } else if (saved.curatedRoast && typeof saved.curatedRoast === 'string') {
          // migrate old single-value format
          curatedRoasts = saved.curatedRoast === 'any' ? ['Drip', 'Espresso'] : [saved.curatedRoast];
        }
        if (Array.isArray(saved.addonItems)) {
          addonItems = mergeAddonItems(saved.addonItems
            .map(a => {
              // Legacy saves keyed add-ons by bare product id; new ones carry productId.
              const productId = a.productId != null
                ? String(a.productId)
                : String(a.id || '').split(':')[0];
              const product = lookupAnyProduct(productId);
              if (!product) return null;
              // A saved roast the product no longer offers is coerced onto one it does,
              // rather than dropped — losing the add-on outright is the worse outcome.
              let roastVal = a.roast || null;
              if (!roastAvailable(product, roastVal)) {
                roastVal = product.hasDrip ? 'Drip' : product.hasEspresso ? 'Espresso' : null;
              }
              // Same for size: coerce onto a size that exists rather than restoring a
              // line that can't be ordered.
              let sizeVal = a.size || '300g';
              if (!sizeVariantAvailable(product, roastVal, sizeVal)) {
                sizeVal = resolveAddonSize(product, roastVal, sizeVal) || sizeVal;
              }
              return {
                id: addonKey(productId, roastVal),
                productId,
                size: sizeVal,
                qty: a.qty || 1,
                roast: roastVal,
                product,
              };
            })
            .filter(Boolean));
        }
        if (saved.addonRoast === 'Drip' || saved.addonRoast === 'Espresso') {
          addonRoastFilter = saved.addonRoast;
        }
        if (saved.currentStep) currentStep = saved.currentStep;
        if (saved.addedToCart) hasAddedToCart = true;
      }
    } catch (_) {}
    restored = true;
  }

  /**
   * Re-derive steps 2 and 3 from the cart.
   *
   * Rebuy's drawer edits the Shopify cart directly, so removing a coffee or changing a
   * quantity there has to flow back into the builder — otherwise the summary keeps
   * showing items that are no longer in the cart. The reactive save below then persists
   * the new selection automatically.
   */
  function syncFromCart(cart) {
    const items = (cart && cart.items) || [];

    const bundleLines = items.filter(l => (l.properties || {})._rc_bundle);
    const addonLines  = items.filter(l => (l.properties || {})._addon);

    const totalBundleQty = bundleLines.reduce((sum, l) => sum + l.quantity, 0);

    if (bundleLines.some(l => (l.properties || {})._curated === 'true')) {
      // Keep the "Curated by our team" choice rather than exploding it into the
      // specific coffees the API happened to resolve.
      coffeeQty = totalBundleQty > 0 ? { curated: totalBundleQty } : {};
    } else {
      const nextQty = {};
      bundleLines.forEach(line => {
        const key = `${line.product_id}:${(line.properties || {})._Roast || ''}`;
        nextQty[key] = (nextQty[key] || 0) + line.quantity;
      });
      coffeeQty = nextQty;
    }

    addonItems = mergeAddonItems(addonLines
      .map(line => {
        const product = lookupAnyProduct(line.product_id);
        if (!product) return null;
        const props = line.properties || {};
        const sizeText = `${props._Size || ''} ${line.variant_title || ''}`;
        const roastVal = props._Roast || null;
        return {
          id:        addonKey(line.product_id, roastVal),
          productId: String(line.product_id),
          product,
          size:      /kilo/i.test(sizeText) ? '1 Kilo' : '300g',
          qty:       line.quantity,
          roast:     roastVal,
        };
      })
      .filter(Boolean));

    subscriptionInCart = bundleLines.length > 0;
    if (!subscriptionInCart) curatedRoasts = [];

    // Don't leave the customer on step 3 once step 2 is no longer satisfied — the step
    // nav disables step 3 in that state, which would otherwise strand them.
    const nextCount = Object.values(coffeeQty).reduce((a, b) => a + b, 0);
    if (currentStep === 3 && (bagCount === 0 || nextCount !== bagCount)) {
      currentStep = 2;
    }
  }

  function cartHasBundle(cart) {
    return ((cart && cart.items) || []).some(l => (l.properties || {})._rc_bundle);
  }

  /**
   * The cart records what was actually added; localStorage records what the customer was
   * building. Let the cart overwrite the selection only when it holds a subscription, or
   * when one was added earlier and has since been emptied. Otherwise arriving with an
   * in-progress, not-yet-added selection would wipe it.
   */
  function onCartChanged(cart) {
    subscriptionInCart = cartHasBundle(cart);
    if (subscriptionInCart || hasAddedToCart) syncFromCart(cart);
  }

  /** Re-read the cart and reconcile. Safe to call on arrival, on tab focus, or on demand. */
  async function reconcileWithCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (res.ok) onCartChanged(await res.json());
    } catch (err) {
      console.warn('[Coava] could not re-read the cart:', err);
    }
  }

  function onCartChangedEvent(event) {
    if (event && event.detail) onCartChanged(event.detail);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') reconcileWithCart();
  }

  function goToStep(n) {
    currentStep = n;
    setTimeout(() => {
      if (sbContainer) {
        const top = sbContainer.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 30);
  }

  let freqSection;

  function selectSize(opt) {
    size.set(opt.val);
    sizeDisplayValue.set(opt.val);
    // Changing the bag count invalidates the per-coffee selection — start fresh.
    coffeeQty = {};
    setTimeout(() => freqSection?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
  }

  $: SIZE_OPTIONS = [
    { val: '1 x 300g',   label: '1 × 300g', price: PRICE_300G,      qty: 1, badge: false, group: '300g' },
    { val: '2 x 300g',   label: '2 × 300g', price: PRICE_300G * 2,  qty: 2, badge: false, group: '300g' },
    { val: '3 x 300g',   label: '3 × 300g', price: PRICE_300G * 3,  qty: 3, badge: false, group: '300g' },
    { val: '4 x 300g',   label: '4 × 300g', price: PRICE_300G * 4,  qty: 4, badge: false, group: '300g' },
    { val: '1 x 1 Kilo', label: '1 × 1 kg', price: PRICE_1KILO,     qty: 1, badge: false, group: '1kg'  },
    { val: '2 x 1 Kilo', label: '2 × 1 kg', price: PRICE_1KILO * 2, qty: 2, badge: false, group: '1kg'  },
    { val: '3 x 1 Kilo', label: '3 × 1 kg', price: PRICE_1KILO * 3, qty: 3, badge: false, group: '1kg'  },
  ];

  $: opts300g = SIZE_OPTIONS.filter(o => o.group === '300g');
  $: opts1kg  = SIZE_OPTIONS.filter(o => o.group === '1kg');

  onMount(async () => {
    restoreSelection();  // restores the saved selection AND currentStep (lands back on step 3)

    const handle = bundleData.productHandle || 'coffee-subscription-plan-1';
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const d = await res.json();
        const plans = (d.selling_plan_groups || [])
          .flatMap(g => g.selling_plans || [])
          .map(p => ({ id: p.id, name: p.name }));
        bundleData = {
          ...bundleData,
          productId: d.id || bundleData.productId,
          variantId: d.variants?.[0]?.id || 0,
          productHandle: d.handle || handle,
          sellingPlans: plans,
        };
      }
    } catch (_) {}

    // Reconcile on arrival. Rebuy's drawer is on every page, so the subscription may have
    // been edited from a product page since we were last here — the cart knows, we don't.
    // This also sets subscriptionInCart, so we update rather than duplicate on the next add.
    await reconcileWithCart();

    // Catches edits made in another tab, or on this page without a reload.
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Catches edits made inside the drawer while we're sitting on this page. main.js runs
    // the single site-wide cart watcher and emits this after its quantity guard has run.
    document.addEventListener('coava:cart-changed', onCartChangedEvent);

    // Lets Rebuy's Event Callbacks push changes instantly instead of waiting for a poll.
    window.coavaSyncBuilderFromCart = reconcileWithCart;
  });

  onDestroy(() => {
    document.removeEventListener('coava:cart-changed', onCartChangedEvent);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (typeof window !== 'undefined' && window.coavaSyncBuilderFromCart === reconcileWithCart) {
      delete window.coavaSyncBuilderFromCart;
    }
  });

  // Processing filter chips — driven by the "Subscription Filters" section in the Shopify
  // theme customizer (Themes → Customize → Subscription page). Falls back to hardcoded defaults.
  const PROCESSING_OPTIONS = (() => {
    const processing = typeof window !== 'undefined' && window.coavaProcessing;
    if (processing && processing.length > 0) {
      return processing.map(p => ({ label: p.label, tags: [p.tag.toLowerCase()] }));
    }
    return [
      { label: 'Washed',      tags: ['washed'] },
      { label: 'Natural',     tags: ['natural'] },
      { label: 'Alternative', tags: ['alternative', 'other'] },
    ];
  })();
  const FLAVOR_OPTIONS = [
    { label: 'Classic', tags: ['classic'] },
    { label: 'Unique',  tags: ['unique'] },
  ];
  // Notes filter chips — driven by the "Subscription Filters" section in the Shopify
  // theme customizer (Themes → Customize → Subscription page). Falls back to hardcoded defaults.
  const DESCRIPTOR_OPTIONS = (() => {
    const notes = typeof window !== 'undefined' && window.coavaNotes;
    if (notes && notes.length > 0) {
      return notes.map(n => ({ label: n.label, tags: [n.tag.toLowerCase()] }));
    }
    return [
      { label: 'Chocolatey', tags: ['chocolate', 'chocolatey'] },
      { label: 'Fruity',    tags: ['fruity'] },
      { label: 'Floral',    tags: ['floral'] },
      { label: 'Syrupy',    tags: ['syrupy'] },
      { label: 'Rich',      tags: ['rich'] },
      { label: 'Delicate',  tags: ['delicate'] },
      { label: 'Complex',   tags: ['complex'] },
    ];
  })();

  function groupMatches(productTags, activeLabels, options) {
    if (!activeLabels.length) return true;
    return activeLabels.some(label => {
      const opt = options.find(o => o.label === label);
      return opt ? opt.tags.some(t => productTags.includes(t)) : false;
    });
  }

  // Returns ordered tag chips: [Flavor, Processing, ...matching Notes]
  // Notes shown = those matching active descriptor filters; falls back to first note if none active.
  function getCardTags(rawTags, activeDescriptors) {
    const tags = (rawTags || []).map(t => t.toLowerCase());
    const result = [];
    const flavorOpt = FLAVOR_OPTIONS.find(o => o.tags.some(t => tags.includes(t)));
    if (flavorOpt) result.push({ type: 'flavor', label: flavorOpt.label });
    const procOpt = PROCESSING_OPTIONS.find(o => o.tags.some(t => tags.includes(t)));
    if (procOpt) result.push({ type: 'processing', label: procOpt.label });
    const allNotes = DESCRIPTOR_OPTIONS.filter(o => o.tags.some(t => tags.includes(t)));
    if (activeDescriptors && activeDescriptors.length > 0) {
      allNotes.filter(o => activeDescriptors.includes(o.label))
              .forEach(o => result.push({ type: 'note', label: o.label }));
    } else if (allNotes.length > 0) {
      result.push({ type: 'note', label: allNotes[0].label });
    }
    return result;
  }

  $: anyFilterActive = !!$roast
    || $processingFilters.length > 0
    || $flavorFilters.length > 0
    || $descriptorFilters.length > 0;

  $: filteredCoffees = coffeeProducts.filter(product => {
    const tags = (product.tags || []).map(t => t.toLowerCase());
    if (!groupMatches(tags, $processingFilters, PROCESSING_OPTIONS)) return false;
    if (!groupMatches(tags, $flavorFilters, FLAVOR_OPTIONS)) return false;
    if (!groupMatches(tags, $descriptorFilters, DESCRIPTOR_OPTIONS)) return false;
    if ($roast === 'Drip'     && !product.hasDrip)     return false;
    if ($roast === 'Espresso' && !product.hasEspresso) return false;
    return true;
  });

  // True when the product supports the currently-selected roast.
  // Products with no roast info at all are always incompatible.
  function roastCompatible(product) {
    if (!product.hasDrip && !product.hasEspresso) return false;
    if ($roast === 'Drip')     return !!product.hasDrip;
    if ($roast === 'Espresso') return !!product.hasEspresso;
    return true;
  }

  // How many bags are required (from step 1) and what size they are.
  $: bagCount     = $sizeDisplayValue ? parseBagCount($sizeDisplayValue) : 0;
  $: bagSizeLabel = ($sizeDisplayValue || '').toLowerCase().includes('kilo') ? '1 Kilo' : '300g';

  // Multi-coffee selection state derived from coffeeQty.
  $: selectedCount = Object.values(coffeeQty).reduce((a, b) => a + b, 0);
  $: hasCurated    = (coffeeQty['curated'] || 0) > 0;
  $: selectedCoffeeList = Object.entries(coffeeQty)
       .filter(([, q]) => q > 0)
       .map(([key, q]) => {
         if (key === 'curated') return { product: CURATED, qty: q, roast: null };
         const colonIdx = key.lastIndexOf(':');
         const productId = colonIdx !== -1 ? key.substring(0, colonIdx) : key;
         const itemRoast = colonIdx !== -1 ? key.substring(colonIdx + 1) : '';
         return { product: lookupProduct(productId), qty: q, roast: itemRoast };
       })
       .filter(e => e.product);
  $: coffeeSummaryText = selectedCoffeeList
       .map(e => e.product.id === 'curated'
         ? 'Curated by our team'
         : (e.qty > 1 ? `${e.product.title} (${e.roast}) ×${e.qty}` : `${e.product.title} (${e.roast})`))
       .join(', ');
  $: roastSummaryText = hasCurated
       ? (curatedRoasts.length > 0 ? curatedRoasts.join(' + ') : '—')
       : [...new Set(selectedCoffeeList.map(e => e.roast).filter(Boolean))].join(' + ') || '—';

  $: {
    const raw = calcPrice($sizeDisplayValue, $shippingFrequencyDisplayValue);
    price           = raw ? formatter.format(raw.total)  : null;
    priceNum        = raw ? raw.total : null;
    perBagPrice     = raw ? formatter.format(raw.perBag) : null;
    discountApplied = raw ? raw.discounted : false;
    // Only meaningful when a discount applies — it renders as the struck-through price,
    // and below MIN_DISCOUNT_BAGS it would equal the price actually charged.
    originalPrice   = (raw && raw.discounted) ? formatter.format(raw.base) : null;
  }

  $: step1Done = !!$sizeDisplayValue && !!$shippingFrequencyDisplayValue;
  $: step2Done = bagCount > 0 && selectedCount === bagCount && (!hasCurated || curatedRoasts.length > 0);
  $: curatedApiAvailable = typeof window !== 'undefined' && !!window.coavaCuratedApiBase;
  $: isReady   = step1Done && step2Done && (!hasCurated || curatedApiAvailable);

  $: progressPct = bagCount > 0 ? Math.min(100, Math.round((selectedCount / bagCount) * 100)) : 0;

  // Add-ons — sourced from the dedicated 'addon-coffees' Shopify collection.
  $: addonCoffees = addonProducts;
  // Same roast filter behaviour as step 2's filteredCoffees, driven by addonRoastFilter.
  $: filteredAddons = addonCoffees.filter(product => {
    if (addonRoastFilter === 'Drip')     return !!product.hasDrip;
    if (addonRoastFilter === 'Espresso') return !!product.hasEspresso;
    return true;
  });
  // key ('productId:Roast') -> qty, for the per-roast steppers on each add-on card.
  $: addonQtyByKey = addonItems.reduce((m, it) => { m[it.id] = it.qty; return m; }, {});
  $: addonEnabled    = addonItems.length > 0;
  $: addonTotalNum   = addonItems.reduce((s, it) => s + getVariantPrice(it.product, it.size) * it.qty, 0);
  $: addonTotalDisplay = addonEnabled ? formatter.format(addonTotalNum) : null;

  $: {
    const rawNum = calcPrice($sizeDisplayValue, $shippingFrequencyDisplayValue);
    const subNum = rawNum ? rawNum.total : 0;
    // With an add-on in the cart, shipping is no longer a known flat rate — it's
    // calculated at checkout, so it can't be folded into a grand total up front.
    grandTotal = subNum > 0
      ? formatter.format(subNum + addonTotalNum + (addonEnabled ? 0 : SHIPPING_FLAT_RATE))
      : null;
  }

  // Save the selection on every change once the initial restore has run.
  $: if (restored && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        roast: $roast, roastDisplay: $roastDisplayValue,
        size: $size, sizeDisplay: $sizeDisplayValue,
        freq: $shippingFrequency, freqDisplay: $shippingFrequencyDisplayValue,
        processing: $processingFilters, flavor: $flavorFilters, descriptor: $descriptorFilters,
        coffeeQty, curatedRoasts,
        addonItems: addonItems.map(it => ({
          id: it.id, productId: it.productId, size: it.size, qty: it.qty, roast: it.roast,
        })),
        addonRoast: addonRoastFilter,
        currentStep,
        addedToCart: hasAddedToCart,
      }));
    } catch (_) {}
  }

  async function fetchSellingPlanId(productHandle, freqDisplayValue) {
    try {
      const res = await fetch(`/products/${productHandle}.js`);
      if (!res.ok) return { id: null };
      const data = await res.json();
      const allPlans = (data.selling_plan_groups || []).flatMap(g => g.selling_plans || []);
      if (!allPlans.length) return { id: null };
      const freqKey = freqDisplayValue.toLowerCase().replace(/[\s-]/g, '');
      const plan = allPlans.find(p => {
        const pname = (p.name || '').toLowerCase().replace(/[\s-]/g, '');
        if (pname === freqKey || pname.includes(freqKey) || freqKey.includes(pname)) return true;
        if (freqKey === 'monthly') return pname.includes('month');
        if (freqKey === 'biweekly') return pname.includes('week') && (pname.includes('2') || pname.includes('bi') || pname.includes('other'));
        return false;
      });
      return plan ? { id: plan.id } : { id: null };
    } catch (e) {
      return { id: null };
    }
  }

  function getCoffeeVariantId(coffeeProduct, selectedRoast, selectedSize) {
    if (!coffeeProduct || coffeeProduct.id === 'curated') return null;
    const variants = coffeeProduct.variants || [];
    if (!variants.length) return null;
    const sizeKey = selectedSize && selectedSize.toLowerCase().includes('kilo') ? 'kilo' : '300g';
    if (selectedRoast && selectedSize) {
      const match = variants.find(v => {
        const opts = ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
        return opts.includes(selectedRoast.toLowerCase()) && opts.includes(sizeKey);
      });
      if (match) return match.id;
    }
    if (selectedRoast) {
      const match = variants.find(v =>
        ((v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase().includes(selectedRoast.toLowerCase())
      );
      if (match) return match.id;
    }
    return variants[0].id;
  }

  function matchPlan(plans, freqKey) {
    if (!plans || !plans.length) return null;
    const plan = plans.find(p => {
      const pname = (p.name || '').toLowerCase().replace(/[\s-]/g, '');
      if (pname === freqKey || pname.includes(freqKey) || freqKey.includes(pname)) return true;
      if (freqKey === 'monthly')  return pname.includes('month');
      if (freqKey === 'biweekly') return pname.includes('week') && (pname.includes('2') || pname.includes('bi') || pname.includes('other'));
      return false;
    });
    return plan ? plan.id : null;
  }

  // Resolve the selling-plan id for a coffee at the chosen frequency.
  async function resolvePlanId(product, freqKey) {
    return matchPlan(product.sellingPlans, freqKey)
      || matchPlan(bundleData.sellingPlans, freqKey)
      || (product.handle ? (await fetchSellingPlanId(product.handle, $shippingFrequencyDisplayValue)).id : null);
  }

  // Subscribe & Save applies from MIN_DISCOUNT_BAGS bags up — a single-bag plan pays the
  // full price. `discounted` drives every "5% off" badge and struck-through price.
  function calcPrice(sizeVal, freqVal) {
    if (!sizeVal) return null;
    const lower = sizeVal.toLowerCase();
    const qty   = parseInt(sizeVal, 10) || 1;
    const unit  = lower.includes('300g') ? PRICE_300G
                : lower.includes('kilo') ? PRICE_1KILO
                : null;
    if (unit === null) return null;
    const base       = unit * qty;
    const discounted = qty >= MIN_DISCOUNT_BAGS;
    const total      = discounted ? base * (1 - DISCOUNT_PCT) : base;
    return { total, perBag: total / qty, discounted, base };
  }

  function parseBagCount(sizeVal) { return parseInt(sizeVal, 10) || 1; }

  function toggleFilter(store, value) {
    store.update(arr => arr.includes(value) ? arr.filter(f => f !== value) : [...arr, value]);
  }

  function clearFilters() {
    roast.set(null);
    roastDisplayValue.set('');
    processingFilters.set([]);
    flavorFilters.set([]);
    descriptorFilters.set([]);
  }

  // ── Step 2: per-coffee quantity (total must equal bagCount) ──
  function addCoffee(product, roast) {
    const next = { ...coffeeQty };
    delete next['curated'];          // a specific coffee replaces "curated"
    const count = Object.values(next).reduce((a, b) => a + b, 0);
    if (count >= bagCount) return;   // already full
    const key = `${product.id}:${roast}`;
    next[key] = (next[key] || 0) + 1;
    coffeeQty = next;
  }

  function removeCoffee(product, roast) {
    const key = `${product.id}:${roast}`;
    if (!coffeeQty[key]) return;
    const next = { ...coffeeQty };
    next[key] -= 1;
    if (next[key] <= 0) delete next[key];
    coffeeQty = next;
  }

  // "Curated" fills every bag and clears specific picks (toggle off to clear).
  function toggleCurated() {
    if (hasCurated) {
      coffeeQty    = {};
      curatedRoasts = [];
    } else {
      coffeeQty = { curated: bagCount };
    }
  }

  function toggleCuratedRoast(r) {
    if (curatedRoasts.includes(r)) {
      curatedRoasts = curatedRoasts.filter(x => x !== r);
    } else {
      curatedRoasts = [...curatedRoasts, r];
    }
  }

  // ── Step 3: add-ons (any number, one-time) ──
  // Picked per roast straight from the card, exactly like step 2's coffee steppers. There
  // is no bag-count cap here — add-ons are one-time extras.
  function addAddon(product, roastVal) {
    if (!roastAvailable(product, roastVal)) return;
    const sizeVal = resolveAddonSize(product, roastVal, bagSizeLabel);
    if (!sizeVal) return;   // no orderable variant in either size
    const key = addonKey(product.id, roastVal);
    if (addonItems.some(it => it.id === key)) {
      addonItems = addonItems.map(it => it.id === key ? { ...it, qty: it.qty + 1 } : it);
    } else {
      addonItems = [...addonItems, {
        id: key,
        productId: String(product.id),
        product,
        size: sizeVal,
        qty: 1,
        roast: roastVal || null,
      }];
    }
  }

  function removeAddon(product, roastVal) {
    const key = addonKey(product.id, roastVal);
    addonItems = addonItems
      .map(it => it.id === key ? { ...it, qty: it.qty - 1 } : it)
      .filter(it => it.qty > 0);
  }

  /** The ✕ on a selected-add-on row — drops the whole line regardless of quantity. */
  function removeAddonItem(item) {
    addonItems = addonItems.filter(it => it.id !== item.id);
  }

  // Add-ons are one-time purchases with no selling plan, so no bag-count rule applies —
  // quantity is uncapped here and in subscriptionCartGuard.js. Changing size keeps the qty.
  function setAddonSize(item, sizeVal) {
    if (!sizeVariantAvailable(item.product, item.roast, sizeVal)) return;
    addonItems = addonItems.map(it =>
      it.id === item.id ? { ...it, size: sizeVal } : it);
  }

  // Floor of 1 only — dropping to 0 is what the remove button is for.
  function changeAddonQty(item, delta) {
    addonItems = addonItems.map(it =>
      it.id === item.id ? { ...it, qty: Math.max(1, it.qty + delta) } : it);
  }

  $: processingPrefText = $processingFilters.length ? $processingFilters.join(', ') : 'No preference';
  $: flavorPrefText     = $flavorFilters.length     ? $flavorFilters.join(', ')     : 'No preference';

  async function handleAddToCart(e) {
    e.preventDefault();
    if (!isReady || submitting) return;
    submitting  = true;
    submitError = '';
    try {
      const bundleHandle = bundleData.productHandle || 'coffee-subscription-plan-1';
      const rcRandom     = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      const rcBundleId   = rcRandom + ':' + (bundleData.productId || '');
      const collectionId = bundleData.collectionId ? String(bundleData.collectionId) : '316537143362';
      const freqNorm     = $shippingFrequencyDisplayValue.toLowerCase().replace(/[\s-]/g, '');
      const rcFreqProps  = freqNorm === 'biweekly'
        ? { freq: '2', unit: 'Weeks' }
        : { freq: '1', unit: 'Months' };

      const cartItems = [];

      // Resolve coffee list — for curated, fetch admin config and look up the matching product
      let resolvedCoffeeList = selectedCoffeeList;
      if (hasCurated) {
        try {
          const apiBase = window.coavaCuratedApiBase;
          const res = await fetch(`${apiBase}/api/curated`);
          if (!res.ok) throw new Error(`Curated API ${res.status}`);
          const curatedData = await res.json();

          // New format: curatedData[`bag${bagCount}`] = { drip: [...N coffees], espresso: [...N coffees] }
          const bagKey  = `bag${bagCount}`;
          const bagData = curatedData[bagKey];
          if (!bagData || typeof bagData !== 'object' || Array.isArray(bagData)) {
            submitError = `Curated selection for ${bagCount} bag(s) isn't configured yet. Please contact us or choose your own coffee.`;
            submitting = false;
            return;
          }

          const resolvedItems = [];

          function resolveFromRoast(roastLower, roastStr, count) {
            const slots = (bagData[roastLower] || []).filter(Boolean);
            if (slots.length < count) {
              submitError = `Not all ${roastStr} curated coffees for ${bagCount} bag(s) are configured. Please contact us or choose your own coffee.`;
              return false;
            }
            for (let si = 0; si < count; si++) {
              const slot = slots[si];
              const prod = coffeeProducts.find(p => String(p.id) === String(slot.shopifyProductId));
              if (!prod) {
                submitError = `A curated coffee isn't available right now. Please choose your own coffee.`;
                return false;
              }
              resolvedItems.push({ product: prod, qty: 1, roast: roastStr });
            }
            return true;
          }

          if (curatedRoasts.length === 2) {
            // Both Drip + Espresso: split half and half
            const dripCount = Math.ceil(bagCount / 2);
            const espCount  = bagCount - dripCount;
            if (!resolveFromRoast('drip', 'Drip', dripCount)) { submitting = false; return; }
            if (espCount > 0 && !resolveFromRoast('espresso', 'Espresso', espCount)) { submitting = false; return; }
          } else if (curatedRoasts.length === 1) {
            const roastStr   = curatedRoasts[0];
            const roastLower = roastStr.toLowerCase();
            if (!resolveFromRoast(roastLower, roastStr, bagCount)) { submitting = false; return; }
          } else {
            submitError = 'Please select a roast preference for your curated selection.';
            submitting = false;
            return;
          }

          if (!resolvedItems.length) {
            submitError = 'Could not resolve curated coffees. Please choose your own coffee.';
            submitting = false;
            return;
          }
          resolvedCoffeeList = resolvedItems;
        } catch (err) {
          submitError = 'Could not load curated selection. Please try again or choose your own coffee.';
          submitting = false;
          return;
        }
      }

      // Subscription coffees — one shared recurring bundle (same _rc_bundle id)
      for (const { product, qty, roast: itemRoast } of resolvedCoffeeList) {
        // A bundle line synced from another surface can arrive with no _Roast. That's
        // "unspecified", not "unavailable" — fall back to a roast this coffee sells
        // rather than stranding the customer. (With no roast, getCoffeeVariantId() skips
        // straight to variants[0] and ignores the bag size too.)
        const roastForLine = itemRoast
          || (product.hasDrip ? 'Drip' : product.hasEspresso ? 'Espresso' : null);

        // Check availability before resolving the variant. The !variantId guard below
        // can't do this on its own: getCoffeeVariantId() falls back to variants[0] for
        // any product that has variants, so an unsold roast/size combination would
        // otherwise resolve to an arbitrary variant and ship the wrong coffee. This also
        // catches a curated slot configured with a coffee that doesn't sell that roast.
        if (!roastAvailable(product, roastForLine) ||
            !sizeVariantAvailable(product, roastForLine, bagSizeLabel)) {
          submitError = `"${product.title}" isn't available as ${roastForLine || 'that roast'} / ${bagSizeLabel}. Please choose another coffee.`;
          submitting = false;
          return;
        }
        const variantId = getCoffeeVariantId(product, roastForLine, $sizeDisplayValue);
        if (!variantId) {
          submitError = `"${product.title}" isn't available as ${roastForLine || 'that roast'} / ${bagSizeLabel}. Please choose another coffee.`;
          submitting = false;
          return;
        }
        const planId = await resolvePlanId(product, freqNorm);
        const item = {
          id:       variantId,
          quantity: qty,
          properties: {
            _rc_bundle:               rcBundleId,
            _rc_bundle_parent:        bundleHandle,
            _rc_bundle_variant:       String(bundleData.variantId || ''),
            _rc_bundle_collection_id: collectionId,
            _shipping_interval_frequency: rcFreqProps.freq,
            _shipping_interval_unit_type: rcFreqProps.unit,
            _Roast:                  roastForLine || '',
            _Coffee:                 product.title,
            _Size:                   `${qty} x ${bagSizeLabel}`,
            // Plan bag count from step 1. Hidden (underscore-prefixed) and repeated on every
            // line so it survives a line being removed from the cart, which is what the
            // quantity cap and the checkout gate measure against.
            _Bags:                   String(bagCount),
            '_Processing preference': processingPrefText,
            '_Flavor preference':     flavorPrefText,
            ...(hasCurated ? { _curated: 'true' } : {}),
          },
        };
        if (planId) item.selling_plan = planId;
        cartItems.push(item);
      }

      // Add-ons — one-time, no selling plan.
      // The roast comes from the card only. Falling back to $roast (step 2's subscription
      // roast) would ask for a roast this coffee may not sell, and getCoffeeVariantId's
      // last-resort variants[0] fallback would then quietly order the wrong variant.
      for (const it of addonItems) {
        if (!roastAvailable(it.product, it.roast)) {
          submitError = `"${it.product.title}" isn't available as ${it.roast || 'that option'}. Please remove it and pick an available roast.`;
          submitting = false;
          return;
        }
        if (!sizeVariantAvailable(it.product, it.roast, it.size)) {
          submitError = `"${it.product.title}" isn't available in ${it.size}. Please pick another size for it.`;
          submitting = false;
          return;
        }
        const variantId = getCoffeeVariantId(it.product, it.roast, it.size);
        if (!variantId) {
          submitError = `"${it.product.title}" isn't available as ${it.roast || 'that option'} / ${it.size}. Please remove it or choose another size.`;
          submitting = false;
          return;
        }
        cartItems.push({
          id:       variantId,
          quantity: it.qty,
          properties: {
            _Roast:  it.roast || '',
            _Coffee: it.product.title,
            _Size:   `${it.qty} x ${it.size}`,
            _addon:  'one-time',
          },
        });
      }

      if (!cartItems.length) {
        submitError = 'Please choose your coffees before adding to cart.';
        submitting = false;
        return;
      }

      // Only ONE subscription bundle is allowed: remove any existing bundle and
      // previously-added builder add-ons so this add replaces (never duplicates) them.
      try {
        const cartRes = await fetch('/cart.js');
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const updates = {};
          for (const line of cart.items || []) {
            const props = line.properties || {};
            if (props._rc_bundle || props._addon) updates[line.key] = 0;
          }
          if (Object.keys(updates).length) {
            await fetch('/cart/update.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json' },
              body:    JSON.stringify({ updates }),
            });
          }
        }
      } catch (_) {}

      const response = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: cartItems }),
      });

      if (response.ok) {
        // From here on the cart is the record of this selection, so later visits should
        // reconcile against it even if the customer empties it from another page.
        hasAddedToCart = true;
        subscriptionInCart = true;

        // Rebuy Smart Cart owns the post-add experience. We never navigate to /cart —
        // if Smart Cart can't be opened it logs why and the customer stays on the
        // builder, where the cart icon count still reflects the add.
        await openRebuyCart();
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('[Coava] cart/add.js error:', err);
        submitError = (err && err.description) ? err.description : `Could not add to cart (${response.status}). Please try again.`;
      }
    } catch (err) {
      console.error('[Coava] handleAddToCart exception:', err);
      submitError = 'Could not add to cart. Please try again.';
    } finally {
      submitting = false;
    }
  }
</script>

<div class="sb" bind:this={sbContainer}>

  <!-- ── Step indicator ── -->
  <div class="sb-steps">
    {#each [
      { n: 1, label: 'How Much' },
      { n: 2, label: 'Your Coffee' },
      { n: 3, label: 'Add-Ons' },
    ] as s}
      <button type="button" class="sb-step"
        class:is-active={currentStep === s.n}
        class:is-done={s.n === 1 ? (step1Done && currentStep !== 1) : s.n === 2 ? (step2Done && currentStep !== 2) : false}
        disabled={s.n === 2 ? !step1Done : s.n === 3 ? !step2Done : false}
        on:click={() => {
          if (s.n === 1 || (s.n === 2 && step1Done) || (s.n === 3 && step2Done)) goToStep(s.n);
        }}>
        <span class="sb-step-circle">{s.n}</span>
        <span class="sb-step-label">{s.label}</span>
      </button>
      {#if s.n < 3}
        <div class="sb-step-line" class:is-done={s.n === 1 ? step1Done : step2Done}></div>
      {/if}
    {/each}
  </div>

  <div class="sb-main">

  <!-- ══════════════════════════════════════════
       STEP 1 — How Much
  ══════════════════════════════════════════ -->
  {#if currentStep === 1}
  <div class="sb-panel">
    <div class="sb-panel-header">
      <p class="sb-panel-meta">Step 1 of 3</p>
      <h2 class="sb-panel-title">How much coffee per shipment?</h2>
      <p class="sb-save-note">Subscribe &amp; Save {DISCOUNT_LABEL.replace(' off', '')} on {MIN_DISCOUNT_BAGS} or more bags</p>
    </div>

    <div class="sb-step1-stack">
      <!-- Row 1: bag size -->
      <div class="sb-step1-size">
        <div class="sb-field-label">Bag Size &amp; Quantity <span class="sb-filter-required">*</span></div>

        <div class="sb-size-group">
          <div class="sb-size-group-label">300g bags</div>
          <div class="sb-size-grid">
            {#each opts300g as opt}
              <button type="button" class="sb-size-tile"
                class:is-selected={$sizeDisplayValue === opt.val}
                on:click={() => selectSize(opt)}>
                <span class="sb-size-main">{opt.label}</span>
                <span class="sb-size-tile-right">
                  {#if opt.qty >= MIN_DISCOUNT_BAGS}
                    <span class="sb-size-orig">{fmtPrice(opt.price)}</span>
                    <span class="sb-size-disc">{fmtPrice(opt.price * (1 - DISCOUNT_PCT))}</span>
                  {:else}
                    <span class="sb-size-disc">{fmtPrice(opt.price)}</span>
                  {/if}
                </span>
              </button>
            {/each}
          </div>
        </div>

        <div class="sb-size-group">
          <div class="sb-size-group-label">1 kg bags</div>
          <div class="sb-size-grid">
            {#each opts1kg as opt}
              <button type="button" class="sb-size-tile"
                class:is-selected={$sizeDisplayValue === opt.val}
                on:click={() => selectSize(opt)}>
                <span class="sb-size-main">{opt.label}</span>
                <span class="sb-size-tile-right">
                  {#if opt.qty >= MIN_DISCOUNT_BAGS}
                    <span class="sb-size-orig">{fmtPrice(opt.price)}</span>
                    <span class="sb-size-disc">{fmtPrice(opt.price * (1 - DISCOUNT_PCT))}</span>
                  {:else}
                    <span class="sb-size-disc">{fmtPrice(opt.price)}</span>
                  {/if}
                </span>
              </button>
            {/each}
          </div>
        </div>
      </div>

      <!-- Row 2: frequency + price -->
      <div class="sb-step1-right" bind:this={freqSection}>
        <div class="sb-field-label" class:needs-attention={!!$sizeDisplayValue && !$shippingFrequencyDisplayValue}>
          Delivery Frequency <span class="sb-filter-required">*</span>
        </div>
        {#if $sizeDisplayValue && !$shippingFrequencyDisplayValue}
          <p class="sb-step-guide">
            <span class="sb-step-guide-arrow" aria-hidden="true">↓</span>
            Choose how often you'd like delivery
          </p>
        {/if}
        <div class="sb-freq-col" class:is-highlight={!!$sizeDisplayValue && !$shippingFrequencyDisplayValue}>
          <button type="button" class="sb-freq-btn"
            class:is-selected={$shippingFrequencyDisplayValue === 'Bi-Weekly'}
            on:click={() => { shippingFrequency.set([2,'Weeks']); shippingFrequencyDisplayValue.set('Bi-Weekly'); }}>
            <span class="sb-freq-main">Bi-Weekly</span>
            <span class="sb-freq-sub">Ships every 2 weeks</span>
          </button>
          <button type="button" class="sb-freq-btn"
            class:is-selected={$shippingFrequencyDisplayValue === 'Monthly'}
            on:click={() => { shippingFrequency.set([1,'Months']); shippingFrequencyDisplayValue.set('Monthly'); }}>
            <span class="sb-freq-main">Monthly</span>
            <span class="sb-freq-sub">Ships once a month</span>
          </button>
        </div>

        {#if price}
          <div class="sb-price-strip">
            <div class="sb-price-line">
              {#if originalPrice}<span class="sb-price-original">{originalPrice}</span>{/if}
              <span class="sb-price-amount">{price}<span class="sb-price-period">/shipment</span></span>
              {#if discountApplied}<span class="sb-badge-discount">{DISCOUNT_LABEL}</span>{/if}
            </div>
            {#if !discountApplied}
              <div class="sb-price-nudge">Add {MIN_DISCOUNT_BAGS - bagCount === 1 ? 'another bag' : `${MIN_DISCOUNT_BAGS - bagCount} more bags`} to save {DISCOUNT_LABEL.replace(' off', '')}</div>
            {/if}
            {#if perBagPrice}<div class="sb-price-perbag">{perBagPrice}/bag · {formatter.format(SHIPPING_FLAT_RATE)} shipping charges</div>{/if}
            {#if priceNum !== null}
              <div class="sb-price-total">Total {formatter.format(priceNum + SHIPPING_FLAT_RATE)}</div>
            {/if}
          </div>
        {/if}
      </div>
    </div>

    <div class="sb-nav">
      <button type="button" class="sb-btn-primary" class:is-ready-pulse={step1Done} disabled={!step1Done} on:click={() => goToStep(2)}>
        Choose Your Coffee →
      </button>
    </div>
  </div>
  {/if}

  <!-- ══════════════════════════════════════════
       STEP 2 — Filters + Coffee picking
  ══════════════════════════════════════════ -->
  {#if currentStep === 2}
  <div class="sb-panel">
    <div class="sb-panel-header">
      <p class="sb-panel-meta">Step 2 of 3</p>
      <h2 class="sb-panel-title">Choose your coffee</h2>
    </div>

    <!-- Filter bar -->
    <div class="sb-filters-bar">
      <div class="sb-filters-header">
        <span class="sb-filters-title">
          Refine
        </span>
        {#if anyFilterActive}
          <button type="button" class="sb-filters-clear" on:click={clearFilters}>✕ Clear filters</button>
        {/if}
      </div>
      <div class="sb-filters-divider"></div>

      <!-- Left: Roast (optional filter, not required), Flavor — carried forward to future orders -->
      <!-- Right: Processing, Notes — this-order-only filters -->
      <div class="sb-filter-grid">
        <div class="sb-filter-col sb-filter-col--carry">
          <div class="sb-filter-col-header">
            <span class="sb-filter-col-title">Coffee Profile</span>
            <span class="sb-filter-col-subtitle">Your selection will be carried forward to your upcoming order.</span>
          </div>
          <div class="sb-filter-group">
            <span class="sb-filter-group-label">Roast</span>
            <div class="sb-segment">
              <button type="button" class="sb-segment-btn"
                class:is-selected={!$roast}
                on:click={() => { roast.set(null); roastDisplayValue.set(''); }}>
                All
              </button>
              <button type="button" class="sb-segment-btn"
                class:is-selected={$roast === 'Drip'}
                on:click={() => { roast.set('Drip'); roastDisplayValue.set('Drip'); }}>
                Drip
              </button>
              <button type="button" class="sb-segment-btn"
                class:is-selected={$roast === 'Espresso'}
                on:click={() => { roast.set('Espresso'); roastDisplayValue.set('Espresso'); }}>
                Espresso
              </button>
            </div>
          </div>

          <div class="sb-filter-group">
            <span class="sb-filter-group-label">Flavor</span>
            <div class="sb-chip-row">
              {#each FLAVOR_OPTIONS as opt}
                <button type="button" class="sb-chip"
                  class:is-selected={$flavorFilters.includes(opt.label)}
                  on:click={() => toggleFilter(flavorFilters, opt.label)}>{opt.label}</button>
              {/each}
            </div>
          </div>
        </div>

        <div class="sb-filter-col sb-filter-col--session">
          <div class="sb-filter-col-header">
            <span class="sb-filter-col-title">Browse Filters</span>
          </div>
          <div class="sb-filter-group">
            <span class="sb-filter-group-label">Processing</span>
            <div class="sb-chip-row">
              {#each PROCESSING_OPTIONS as opt}
                <button type="button" class="sb-chip"
                  class:is-selected={$processingFilters.includes(opt.label)}
                  on:click={() => toggleFilter(processingFilters, opt.label)}>{opt.label}</button>
              {/each}
            </div>
          </div>

          <div class="sb-filter-group">
            <span class="sb-filter-group-label">Notes</span>
            <div class="sb-chip-row">
              {#each DESCRIPTOR_OPTIONS as opt}
                <button type="button" class="sb-chip"
                  class:is-selected={$descriptorFilters.includes(opt.label)}
                  on:click={() => toggleFilter(descriptorFilters, opt.label)}>{opt.label}</button>
              {/each}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Progress: bags chosen vs. the count picked in step 1 -->
    <div class="sb-progress">
      <div class="sb-progress-head">
        <span class="sb-progress-title">Pick your {bagCount} {bagCount === 1 ? 'bag' : 'bags'}</span>
        <span class="sb-progress-count" class:is-complete={step2Done}>{selectedCount} of {bagCount} selected</span>
      </div>
      <div class="sb-progress-track">
        <div class="sb-progress-fill" style="width:{progressPct + '%'}"></div>
      </div>
      {#if selectedCount < bagCount}
        <p class="sb-progress-hint">
          <span class="sb-step-guide-arrow" aria-hidden="true">↓</span>
          {bagCount - selectedCount === 1 ? 'Add 1' : `Add ${bagCount - selectedCount} more`} — mix Drip &amp; Espresso freely, or repeat a coffee with "+".
        </p>
      {:else if hasCurated && curatedRoasts.length === 0}
        <p class="sb-progress-hint">Choose your roast preference for the curated selection below.</p>
      {:else}
        <p class="sb-progress-hint sb-progress-hint--done">✓ {bagCount === 1 ? '1 bag' : `All ${bagCount} bags`} chosen — you're ready for the next step.</p>
      {/if}
    </div>

    <!-- Coffee grid -->
    <div class="sb-coffee-grid">
      <!-- Curated fills every bag; mutually exclusive with specific picks -->
      <div class="sb-coffee-card" class:is-selected={hasCurated}
        role="button" tabindex="0"
        on:click={toggleCurated}
        on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCurated(); } }}>
        <div class="sb-coffee-img">
          <img src="https://cdn.shopify.com/s/files/1/0057/2942/files/Curated4.jpg?v=1786444881" alt="Curated" />
          {#if hasCurated}<span class="sb-coffee-check" aria-label="Selected">✓</span>{/if}
        </div>
        <div class="sb-coffee-info">
          <div class="sb-coffee-name">Curated for me</div>
          <div class="sb-coffee-tag-row"><span class="sb-coffee-tag">{bagCount === 1 ? 'We pick it for you' : `We pick all ${bagCount} for you`}</span></div>
          {#if hasCurated}
            <div class="sb-curated-roast-row">
              <span class="sb-curated-roast-label">Roast preference:</span>
              <button type="button" class="sb-roast-chip" class:is-active={curatedRoasts.includes('Drip')}
                on:click|stopPropagation={() => toggleCuratedRoast('Drip')}>Drip</button>
              <button type="button" class="sb-roast-chip" class:is-active={curatedRoasts.includes('Espresso')}
                on:click|stopPropagation={() => toggleCuratedRoast('Espresso')}>Espresso</button>
            </div>
            {#if curatedRoasts.length === 0}
              <p class="sb-curated-roast-hint">Please select at least one roast preference above.</p>
            {/if}
            <button type="button" class="sb-curated-remove"
              on:click|stopPropagation={toggleCurated}>Remove</button>
          {/if}
        </div>
      </div>

      {#each filteredCoffees as product (product.id)}
        {@const dripQty    = coffeeQty[`${product.id}:Drip`]    || 0}
        {@const espQty     = coffeeQty[`${product.id}:Espresso`] || 0}
        {@const totalQty   = dripQty + espQty}
        {@const compatible = roastCompatible(product)}
        <div class="sb-coffee-card" class:is-selected={totalQty > 0} class:is-roast-disabled={!compatible}>
          <div class="sb-coffee-img">
            <img src={getProductImage(product, bagSizeLabel) || 'https://uploads.coavacoffee.com/production/uploads/subscriptions/subscription_profile_01.jpg'}
              alt={product.title} />
            {#if totalQty > 0}<span class="sb-coffee-qty-badge" aria-label="{totalQty} selected">{totalQty}</span>{/if}
            {#if !compatible}
              <div class="sb-roast-unavail">Not available</div>
            {:else if !(product.hasDrip && product.hasEspresso)}
              <!-- Sells one roast only — say so, so the single stepper below doesn't look
                   like a rendering glitch. The !compatible branch above can't report this:
                   filteredCoffees has already dropped anything failing the active filter,
                   so by here !compatible means "no roast options at all". -->
              <div class="sb-roast-unavail">
                {product.hasDrip ? 'Drip only' : 'Espresso only'}
              </div>
            {/if}
          </div>
          <div class="sb-coffee-info">
            <div class="sb-coffee-name">{product.title}</div>
            {#if product.tags && product.tags.length}
              {#if getCardTags(product.tags, $descriptorFilters).length}
                <div class="sb-coffee-tag-row">
                  {#each getCardTags(product.tags, $descriptorFilters) as tagInfo}
                    <span class="sb-coffee-tag sb-coffee-tag--{tagInfo.type}">{tagInfo.label}</span>
                  {/each}
                </div>
              {/if}
            {/if}
            <div class="sb-coffee-roasts">
              {#if product.hasDrip}
                <div class="sb-stepper-row">
                  <span class="sb-roast-label">Drip</span>
                  <div class="sb-roast-stepper">
                    <button type="button" class="sb-qty-btn" disabled={dripQty === 0 || !compatible}
                      aria-label="Remove Drip" on:click|stopPropagation={() => removeCoffee(product, 'Drip')}>−</button>
                    <span class="sb-qty-val">{dripQty}</span>
                    <button type="button" class="sb-qty-btn" disabled={!compatible || selectedCount >= bagCount}
                      aria-label="Add Drip" on:click|stopPropagation={() => compatible && addCoffee(product, 'Drip')}>+</button>
                  </div>
                </div>
              {/if}
              {#if product.hasEspresso}
                <div class="sb-stepper-row">
                  <span class="sb-roast-label">Espresso</span>
                  <div class="sb-roast-stepper">
                    <button type="button" class="sb-qty-btn" disabled={espQty === 0 || !compatible}
                      aria-label="Remove Espresso" on:click|stopPropagation={() => removeCoffee(product, 'Espresso')}>−</button>
                    <span class="sb-qty-val">{espQty}</span>
                    <button type="button" class="sb-qty-btn" disabled={!compatible || selectedCount >= bagCount}
                      aria-label="Add Espresso" on:click|stopPropagation={() => compatible && addCoffee(product, 'Espresso')}>+</button>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/each}

      {#if filteredCoffees.length === 0}
        <p class="sb-no-results">No coffees match the selected filters.</p>
      {/if}
    </div>

    <div class="sb-nav sb-nav--split sb-nav--sticky">
      <button type="button" class="sb-btn-secondary" on:click={() => goToStep(1)}>← Back</button>
      {#if selectedCount > 0}
        <div class="sb-selection-badge" class:is-complete={step2Done}>
          <span class="sb-sel-check">{step2Done ? '✓' : selectedCount}</span>
          <div class="sb-sel-info">
            <span class="sb-sel-count">{selectedCount} of {bagCount} {bagCount === 1 ? 'bag' : 'bags'}</span>
            <span class="sb-sel-name">{hasCurated ? 'Curated by our team' : coffeeSummaryText}</span>
          </div>
        </div>
      {/if}
      <button type="button" class="sb-btn-primary" class:is-ready-pulse={step2Done} disabled={!step2Done} on:click={() => goToStep(3)}>
        Add-Ons &amp; Checkout →
      </button>
    </div>
  </div>
  {/if}

  <!-- ══════════════════════════════════════════
       STEP 3 — Add-Ons + Order Summary
  ══════════════════════════════════════════ -->
  {#if currentStep === 3}
  <div class="sb-panel">
    <div class="sb-panel-header">
      <p class="sb-panel-meta">Step 3 of 3</p>
      <h2 class="sb-panel-title">Add-Ons <span class="sb-optional-tag">Optional</span></h2>
    </div>

    <!-- Mini summary bar -->
    <div class="sb-summary-bar">
      <span class="sb-summary-pill">{$sizeDisplayValue}</span>
      <span class="sb-summary-sep">·</span>
      <span class="sb-summary-pill">{$shippingFrequencyDisplayValue}</span>
      <span class="sb-summary-sep">·</span>
      <span class="sb-summary-pill">{roastSummaryText}</span>
      {#if coffeeSummaryText || hasCurated}
        <span class="sb-summary-sep">·</span>
        <span class="sb-summary-pill sb-summary-pill--coffee">{hasCurated ? 'Curated by our team' : coffeeSummaryText}</span>
      {/if}
      {#if price}
        <span class="sb-summary-price">
          {price}/shipment
        </span>
      {/if}
    </div>

    <!-- Addon intro -->
    <p class="sb-addon-intro">Want extra one-time bags with your first order? Pick any coffees below — they won't recur. Add as many as you like.</p>

    <!-- Roast filter — same segmented control as step 2, scoped to the add-on grid -->
    {#if addonCoffees.length > 0}
      <div class="sb-filters-bar">
        <div class="sb-filters-header">
          <span class="sb-filters-title">Refine add-ons</span>
          {#if addonRoastFilter}
            <button type="button" class="sb-filters-clear" on:click={() => (addonRoastFilter = null)}>✕ Clear filter</button>
          {/if}
        </div>

        <div class="sb-filters-divider"></div>

        <div class="sb-filter-group">
          <span class="sb-filter-group-label">Roast</span>
          <div class="sb-segment">
            <button type="button" class="sb-segment-btn"
              class:is-selected={!addonRoastFilter}
              on:click={() => (addonRoastFilter = null)}>
              All
            </button>
            <button type="button" class="sb-segment-btn"
              class:is-selected={addonRoastFilter === 'Drip'}
              on:click={() => (addonRoastFilter = 'Drip')}>
              Drip
            </button>
            <button type="button" class="sb-segment-btn"
              class:is-selected={addonRoastFilter === 'Espresso'}
              on:click={() => (addonRoastFilter = 'Espresso')}>
              Espresso
            </button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Addon grid — roast picked per card, exactly like step 2 -->
    {#if addonCoffees.length > 0}
      <div class="sb-carousel">
        {#each filteredAddons as product (product.id)}
          {@const dripQty  = addonQtyByKey[`${product.id}:Drip`]     || 0}
          {@const espQty   = addonQtyByKey[`${product.id}:Espresso`] || 0}
          {@const plainQty = addonQtyByKey[`${product.id}:`]         || 0}
          {@const hasRoasts = product.hasDrip || product.hasEspresso}
          {@const totalQty = dripQty + espQty + plainQty}
          <div class="sb-carousel-card" class:is-selected={totalQty > 0}>
            <div class="sb-carousel-img">
              <img src={getProductImage(product, bagSizeLabel) || 'https://uploads.coavacoffee.com/production/uploads/subscriptions/subscription_profile_01.jpg'}
                alt={product.title} />
              {#if totalQty > 0}
                <span class="sb-coffee-qty-badge" aria-label="{totalQty} selected">{totalQty}</span>
              {/if}
              {#if hasRoasts && !(product.hasDrip && product.hasEspresso)}
                <!-- Only one roast is sold for this coffee — say so, the way step 2 does,
                     so the single stepper below doesn't look like a rendering glitch. -->
                <div class="sb-roast-unavail">
                  {product.hasDrip ? 'Drip only' : 'Espresso only'}
                </div>
              {/if}
            </div>
            <div class="sb-coffee-info">
              <div class="sb-coffee-name">{product.title}</div>
              {#if product.tags && product.tags.length}
                {#if getCardTags(product.tags, $descriptorFilters).length}
                  <div class="sb-coffee-tag-row">
                    {#each getCardTags(product.tags, $descriptorFilters) as tagInfo}
                      <span class="sb-coffee-tag sb-coffee-tag--{tagInfo.type}">{tagInfo.label}</span>
                    {/each}
                  </div>
                {/if}
              {/if}
              <div class="sb-coffee-roasts">
                {#if product.hasDrip}
                  <div class="sb-stepper-row">
                    <span class="sb-roast-label">Drip</span>
                    <div class="sb-roast-stepper">
                      <button type="button" class="sb-qty-btn" disabled={dripQty === 0}
                        aria-label="Remove Drip add-on" on:click={() => removeAddon(product, 'Drip')}>−</button>
                      <span class="sb-qty-val">{dripQty}</span>
                      <button type="button" class="sb-qty-btn"
                        disabled={!addonOrderable(product, 'Drip', bagSizeLabel)}
                        aria-label="Add Drip add-on" on:click={() => addAddon(product, 'Drip')}>+</button>
                    </div>
                  </div>
                {/if}
                {#if product.hasEspresso}
                  <div class="sb-stepper-row">
                    <span class="sb-roast-label">Espresso</span>
                    <div class="sb-roast-stepper">
                      <button type="button" class="sb-qty-btn" disabled={espQty === 0}
                        aria-label="Remove Espresso add-on" on:click={() => removeAddon(product, 'Espresso')}>−</button>
                      <span class="sb-qty-val">{espQty}</span>
                      <button type="button" class="sb-qty-btn"
                        disabled={!addonOrderable(product, 'Espresso', bagSizeLabel)}
                        aria-label="Add Espresso add-on" on:click={() => addAddon(product, 'Espresso')}>+</button>
                    </div>
                  </div>
                {/if}
                {#if !hasRoasts}
                  <!-- No roast options on this product — a single plain quantity stepper -->
                  <div class="sb-stepper-row">
                    <span class="sb-roast-label">Add</span>
                    <div class="sb-roast-stepper">
                      <button type="button" class="sb-qty-btn" disabled={plainQty === 0}
                        aria-label="Remove add-on" on:click={() => removeAddon(product, null)}>−</button>
                      <span class="sb-qty-val">{plainQty}</span>
                      <button type="button" class="sb-qty-btn"
                        disabled={!addonOrderable(product, null, bagSizeLabel)}
                        aria-label="Add add-on" on:click={() => addAddon(product, null)}>+</button>
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>

      {#if filteredAddons.length === 0}
        <p class="sb-no-results">No add-on coffees match the selected roast.</p>
      {/if}
    {:else}
      <p class="sb-no-results">No additional coffees available as add-ons.</p>
    {/if}

    <!-- Selected add-ons — one configurable row each -->
    {#if addonItems.length}
      <div class="sb-addon-list">
        {#each addonItems as item (item.id)}
          <div class="sb-addon-row">
            <span class="sb-addon-row-name">{item.product.title}</span>
            <!-- Roast is picked on the card; shown here read-only so two lines of the
                 same coffee (Drip and Espresso) stay tellable apart. -->
            {#if item.roast}
              <span class="sb-addon-row-roast">{item.roast}</span>
            {/if}
            <!-- Size chips — a size this coffee doesn't sell is disabled, and shows no
                 price, since getVariantPrice() would invent the flat fallback one. -->
            <div class="sb-chip-row">
              {#each [{ val: '300g', label: '300g' }, { val: '1 Kilo', label: '1 kg' }] as opt}
                {@const sizeOk = sizeVariantAvailable(item.product, item.roast, opt.val)}
                <button type="button" class="sb-chip"
                  class:is-selected={item.size === opt.val}
                  disabled={!sizeOk}
                  title={sizeOk ? '' : `${item.product.title} isn't sold in ${opt.label}`}
                  on:click={() => setAddonSize(item, opt.val)}>
                  {opt.label} — {sizeOk ? formatter.format(getVariantPrice(item.product, opt.val)) : 'unavailable'}
                </button>
              {/each}
            </div>
            <div class="sb-qty-row">
              <button type="button" class="sb-qty-btn" disabled={item.qty <= 1}
                on:click={() => changeAddonQty(item, -1)}>−</button>
              <span class="sb-qty-val">{item.qty}</span>
              <button type="button" class="sb-qty-btn"
                on:click={() => changeAddonQty(item, 1)}>+</button>
            </div>
            <span class="sb-addon-row-price">{formatter.format(getVariantPrice(item.product, item.size) * item.qty)}</span>
            <button type="button" class="sb-addon-remove" aria-label="Remove add-on"
              on:click={() => removeAddonItem(item)}>✕</button>
          </div>
        {/each}
      </div>
    {/if}

    <!-- ── Order Summary ── -->
    <div class="sb-order-summary">
      <div class="sb-order-summary-title">Your Order</div>

      <!-- Hidden: Grind Preference and Special Requests
      <div class="sb-extras">
        <label class="sb-extra-label">
          <span class="sb-field-label">Grind Preference</span>
          <div class="form-select">
            <select class="form-select-input" name="properties[Grind]">
              <option value="">Whole Bean (Default)</option>
              <option value="Drip (Medium)">Grind – Drip (Medium)</option>
              <option value="Espresso (Fine)">Grind – Espresso (Fine)</option>
              <option value="French Press (Coarse)">Grind – French Press (Coarse)</option>
              <option value="Pour Over (Medium Fine)">Grind – Pour Over (Medium Fine)</option>
            </select>
            <div class="form-select-arrow"></div>
          </div>
        </label>
        <label class="sb-extra-label">
          <span class="sb-field-label">Special Requests</span>
          <textarea class="form-textarea" name="properties[Requests]" rows="2"
            placeholder="Any special requests?"></textarea>
        </label>
      </div>
      -->

      {#if price}
        <div class="sb-total-row">
          <span class="sb-total-label">Subscription</span>
          <span class="sb-total-val">
            {#if originalPrice}<span class="sb-price-original">{originalPrice}</span>{/if}
            {price}/shipment {#if discountApplied}<span class="sb-badge-discount">{DISCOUNT_LABEL}</span>{/if}
          </span>
        </div>
      {/if}
      {#if addonTotalDisplay}
        <div class="sb-total-row">
          <span class="sb-total-label">One-time add-ons ({addonItems.length})</span>
          <span class="sb-total-val">{addonTotalDisplay} <span class="sb-onetime-badge">one-time</span></span>
        </div>
      {/if}
      <div class="sb-total-row">
        <span class="sb-total-label">Shipping</span>
        <span class="sb-total-val">{addonEnabled ? 'Calculated at checkout' : formatter.format(SHIPPING_FLAT_RATE)}</span>
      </div>
      {#if grandTotal}
        <div class="sb-total-row sb-total-row--grand">
          <span class="sb-total-label sb-grand-label">Grand Total</span>
          <span class="sb-total-val sb-grand-val">{grandTotal}</span>
        </div>
      {/if}
    </div>

    {#if hasCurated && !curatedApiAvailable}
      <p class="sb-curated-note">Curated subscriptions require manual setup — please contact us to finalize.</p>
    {/if}

    {#if submitError}
      <p class="sb-error">{submitError}</p>
    {/if}

    <div class="sb-nav sb-nav--split sb-nav--sticky">
      <button type="button" class="sb-btn-secondary" on:click={() => goToStep(2)}>← Back</button>
      <button type="button" class="sb-btn-cta js-add-subscription"
        class:is-ready-pulse={isReady && !submitting}
        disabled={!isReady || submitting}
        on:click={handleAddToCart}>
        {submitting ? 'Adding…' : subscriptionInCart ? 'Update My Subscription' : 'Add to Cart'}
      </button>
    </div>

    {#if debug}
      <HiddenField {debug} name="debug_bagCount"  value={String(bagCount)} />
      <HiddenField {debug} name="debug_selected"  value={String(selectedCount)} />
      <HiddenField {debug} name="debug_coffees"   value={coffeeSummaryText} />
      <HiddenField {debug} name="debug_roast"     value={$roast} />
      <HiddenField {debug} name="debug_size"      value={$sizeDisplayValue} />
      <HiddenField {debug} name="debug_frequency" value={$shippingFrequencyDisplayValue} />
      <HiddenField {debug} name="debug_addons"    value={addonItems.map(a => `${a.product.title} ${a.qty}x${a.size}`).join('; ')} />
    {/if}
  </div>
  {/if}

  </div><!-- /sb-main -->
</div>

<style>
  /* ── Base: sidebar grid layout ── */
  .sb {
    width: 100%;
    max-width: none;
    margin: 0 auto;
    padding: 0 clamp(24px, 5vw, 90px) 48px;
    font-family: inherit;
    font-size: 18px;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: start;
    gap: 0 28px;
  }
  .sb-main { min-width: 0; }

  /* ── Step indicator — vertical left sidebar ── */
  .sb-steps {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-top: 28px;
    padding-right: 0;
    border-right: 2px solid #ede5db;
    margin-right: 0;
    position: sticky;
    top: 20px;
  }
  .sb-step {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 5px 12px 5px 0;
    opacity: 0.32;
    transition: opacity 0.2s;
    text-align: left;
    width: 100%;
  }
  .sb-step:disabled { cursor: default; }
  .sb-step.is-active, .sb-step.is-done { opacity: 1; }
  .sb-step-circle {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid #c8b8a8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.82em;
    font-weight: 800;
    color: #7a6a5a;
    background: white;
    transition: background 0.25s, border-color 0.25s, color 0.25s;
  }
  .sb-step.is-active .sb-step-circle {
    background: #3b2314;
    border-color: #3b2314;
    color: white;
  }
  .sb-step.is-done .sb-step-circle { background: #c2976a; border-color: #c2976a; color: white; }
  .sb-step-label {
    font-size: 0.62em;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #9c8878;
    white-space: nowrap;
    font-weight: 600;
    line-height: 1.3;
  }
  .sb-step.is-active .sb-step-label { color: #3b2314; font-weight: 800; }
  .sb-step.is-done   .sb-step-label { color: #c2976a; }
  /* vertical connector — centered under the 34px circle (17px - 1px = 16px left) */
  .sb-step-line {
    width: 2px;
    height: 22px;
    background: #ede5db;
    margin: 1px 0 1px 16px;
    flex-shrink: 0;
    transition: background 0.4s;
  }
  .sb-step-line.is-done { background: #c2976a; }

  /* ── Panel ── */
  .sb-panel {
    background: #fff;
    border: 1px solid #e8ddd2;
    border-radius: 14px;
    padding: 24px 28px 20px;
    box-shadow: 0 4px 24px rgba(59,35,20,0.07), 0 1px 4px rgba(59,35,20,0.04);
  }

  /* ── Panel header ── */
  .sb-panel-header { margin-bottom: 20px; }
  .sb-save-note {
    display: inline-block;
    margin: 8px 0 0;
    font-size: 0.76em;
    font-weight: 600;
    color: #be1e2d;
    background: rgba(190, 30, 45, 0.07);
    padding: 4px 12px;
    border-radius: 20px;
  }
  .sb-panel-meta {
    font-size: 0.75em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #c2976a;
    margin: 0 0 6px;
  }
  .sb-panel-title {
    font-size: 1.75em;
    font-weight: 800;
    color: #3b2314;
    margin: 0;
    line-height: 1.15;
    font-family: "din-condensed-web", "Helvetica Neue", Helvetica, Arial, sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  /* ── Field label ── */
  .sb-field-label {
    font-size: 0.73em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #7a6a5a;
    margin-bottom: 10px;
    display: block;
    white-space: nowrap;
  }

  /* ── Step 1: stacked layout ── */
  .sb-step1-stack {
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  /* Size group labels */
  .sb-size-group { margin-bottom: 4px; }
  .sb-size-group + .sb-size-group { margin-top: 12px; }
  .sb-size-group-label {
    font-size: 0.7em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #9c8878;
    margin-bottom: 8px;
  }

  /* Size grid: compact tiles that wrap 3–4 per row without stretching */
  .sb-size-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 260px));
    gap: 8px;
  }
  @media (max-width: 480px) {
    .sb-size-grid { grid-template-columns: 1fr; }
  }
  .sb-size-tile {
    border: 2px solid #e8ddd2;
    border-radius: 10px;
    padding: 10px 14px;
    min-height: 54px;
    cursor: pointer;
    background: white;
    transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    text-align: left;
    position: relative;
    width: 100%;
    box-sizing: border-box;
  }
  .sb-size-tile:hover {
    border-color: #c2976a;
    box-shadow: 0 2px 8px rgba(194,151,106,0.22);
  }
  .sb-size-tile.is-selected {
    background: #3b2314;
    border-color: #3b2314;
    box-shadow: 0 4px 14px rgba(59,35,20,0.22);
  }
  .sb-size-main {
    font-size: 0.92em;
    font-weight: 800;
    color: #3b2314;
    transition: color 0.18s;
    white-space: nowrap;
  }
  .sb-size-tile.is-selected .sb-size-main { color: white; }
  .sb-size-tile-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
  }
  .sb-size-orig {
    font-size: 0.72em;
    color: #a89a8a;
    text-decoration: line-through;
    white-space: nowrap;
    transition: color 0.18s;
  }
  .sb-size-disc {
    font-size: 0.85em;
    font-weight: 700;
    color: #9f6a33;
    white-space: nowrap;
    transition: color 0.18s;
  }
  .sb-size-tile.is-selected .sb-size-orig { color: rgba(255,255,255,0.55); }
  .sb-size-tile.is-selected .sb-size-disc { color: #ffffff; }

  /* Frequency: stacked column in right panel */
  .sb-freq-col { display: flex; flex-direction: column; gap: 8px; }
  .sb-freq-btn {
    border: 1.5px solid #ddd;
    border-radius: 7px;
    padding: 11px 14px;
    text-align: left;
    cursor: pointer;
    background: white;
    transition: border-color 0.15s, background 0.15s;
    width: 100%;
  }
  .sb-freq-btn:hover      { border-color: #c2976a; box-shadow: 0 2px 8px rgba(194,151,106,0.22); }
  .sb-freq-btn.is-selected {
    background: #3b2314;
    border-color: #3b2314;
    box-shadow: 0 4px 14px rgba(59,35,20,0.22);
  }
  .sb-freq-main { display: block; font-size: 0.9em; font-weight: 700; color: #3b2314; transition: color 0.15s; }
  .sb-freq-sub  { display: block; font-size: 0.76em; color: #7a6a5a; margin-top: 2px; transition: color 0.15s; }
  .sb-freq-btn.is-selected .sb-freq-main { color: white; }
  .sb-freq-btn.is-selected .sb-freq-sub  { color: rgba(255,255,255,0.72); }

  /* Price strip */
  .sb-price-strip {
    margin-top: 14px;
    padding: 12px 14px;
    background: #f9f4ef;
    border-radius: 7px;
  }
  .sb-price-line { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .sb-price-amount { font-size: 1.25em; font-weight: 700; color: #3b2314; }
  .sb-price-period { font-size: 0.72em; font-weight: 400; color: #7a6a5a; }
  .sb-badge-discount {
    display: inline-block;
    background: #be1e2d;
    color: white;
    font-size: 0.72em;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 600;
    margin-left: 6px;
  }
  .sb-freq-btn.is-selected .sb-badge-discount { background: rgba(255,255,255,0.22); }
  .sb-price-original {
    font-size: 0.82em;
    color: #9c8878;
    text-decoration: line-through;
    margin-right: 4px;
    font-weight: 400;
  }
  .sb-price-perbag { font-size: 0.78em; color: #7a6a5a; margin-top: 3px; }
  /* Shown below MIN_DISCOUNT_BAGS — tells the customer what unlocks the discount */
  .sb-price-nudge {
    font-size: 0.78em;
    font-weight: 600;
    color: #b07c2c;
    margin-top: 5px;
  }
  .sb-price-total {
    font-size: 0.88em;
    font-weight: 700;
    color: #3b2314;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e8ddd2;
  }

  /* ── Step 2: Filter bar ── */
  .sb-filters-bar {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 20px;
    border: 1.5px solid #e3d8cc;
    border-radius: 12px;
    padding: 16px 18px;
    background: linear-gradient(135deg, #fdf9f5 0%, #f8f2eb 100%);
    box-shadow: 0 2px 8px rgba(59,35,20,0.05);
  }

  /* Header row */
  .sb-filters-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sb-filters-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.11em;
    color: #b07c2c;
  }
  .sb-filters-clear {
    border: none;
    background: none;
    font-size: 0.76em;
    font-weight: 600;
    color: #be1e2d;
    cursor: pointer;
    padding: 3px 8px;
    border-radius: 20px;
    transition: background 0.15s;
  }
  .sb-filters-clear:hover { background: rgba(190,30,45,0.08); }
  .sb-filters-divider {
    height: 1px;
    background: linear-gradient(to right, #e3d8cc, transparent);
    margin: -4px 0 0;
  }

  /* Filter group */
  .sb-filter-group { display: flex; flex-direction: column; gap: 8px; }
  .sb-filter-group-label {
    font-size: 0.68em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #9c8878;
  }
  .sb-filter-required { color: #c2976a; font-weight: 900; }

  /* Segmented control for Roast */
  .sb-segment {
    display: inline-flex;
    border: 1.5px solid #d9cfc5;
    border-radius: 8px;
    overflow: hidden;
    background: white;
    width: fit-content;
  }
  .sb-segment-btn {
    padding: 7px 22px;
    font-size: 0.88em;
    font-weight: 700;
    cursor: pointer;
    background: transparent;
    color: #7a6a5a;
    border: none;
    transition: background 0.15s, color 0.15s;
    letter-spacing: 0.02em;
  }
  .sb-segment-btn + .sb-segment-btn { border-left: 1.5px solid #d9cfc5; }
  .sb-segment-btn:hover:not(.is-selected) { background: #f5ede3; color: #3b2314; }
  .sb-segment-btn.is-selected {
    background: #3b2314;
    color: white;
  }
  /* Draw the eye to the roast control until a roast is chosen
     — same target-pulse as the frequency buttons in step 1 */
  .sb-segment.is-attention {
    border-color: #9f6a33;
    animation: sb-target-pulse 1.8s ease-in-out infinite;
  }

  /* Filter grid: left col = Roast + Flavor (carried forward), right col = Processing + Notes */
  .sb-filter-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px 24px;
    padding-top: 2px;
  }
  .sb-filter-col {
    display: flex;
    flex-direction: column;
    gap: 14px;
    border-radius: 10px;
    padding: 14px 16px;
    background: #ffffff;
    border: 1.5px solid #e3d8cc;
  }
  .sb-filter-col-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .sb-filter-col-title {
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7a6a5a;
  }
  .sb-filter-col-subtitle {
    font-size: 0.74em;
    color: #9c8878;
  }
  @media (max-width: 1024px) {
    .sb-filter-grid { grid-template-columns: 1fr; }
  }

  /* Chips */
  .sb-chip-row { display: flex; gap: 5px; flex-wrap: wrap; align-items: center; }
  .sb-chip {
    border: 1.5px solid #ddd;
    border-radius: 20px;
    padding: 4px 11px;
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    background: white;
    color: #5a4a3a;
    white-space: nowrap;
    transition: border-color 0.15s, background 0.15s, color 0.15s, box-shadow 0.15s;
  }
  .sb-chip:hover:not(:disabled):not(.is-selected) {
    border-color: #c2976a;
    background: #fdf5ea;
    color: #3b2314;
  }
  /* A size this coffee isn't sold in — visibly inert, not just unresponsive */
  .sb-chip:disabled {
    opacity: 0.42;
    cursor: not-allowed;
    background: #f4f1ec;
    text-decoration: line-through;
  }
  .sb-chip.is-selected:hover {
    border-color: #96692a;
    background: #96692a;
  }
  .sb-chip.is-selected {
    border-color: #b07c2c;
    background: #b07c2c;
    color: white;
    box-shadow: 0 2px 6px rgba(176,124,44,0.28);
  }

  /* Coffee grid: max 4 per row with larger cards/images */
  .sb-coffee-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  /* Card — flush image at top, info below */
  .sb-coffee-card {
    border: 1.5px solid #dccfbf;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    background: white;
    text-align: center;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 6px rgba(59,35,20,0.07);
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.18s;
  }
  .sb-coffee-card:hover {
    border-color: #c2976a;
    box-shadow: 0 8px 22px rgba(194,151,106,0.2);
    transform: translateY(-3px);
  }
  .sb-coffee-card.is-selected {
    border: 2px solid #9f6a33;
    box-shadow: 0 8px 22px rgba(159,106,51,0.24);
    transform: translateY(-3px);
  }

  /* Roast-incompatible card — shown grayed out, no hover lift */
  .sb-coffee-card.is-roast-disabled {
    opacity: 0.48;
    cursor: default;
  }
  .sb-coffee-card.is-roast-disabled:hover {
    border-color: #dccfbf;
    box-shadow: 0 1px 6px rgba(59,35,20,0.07);
    transform: none;
  }
  /* Roast-unavailable banner inside the image zone */
  .sb-roast-unavail {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(30, 20, 10, 0.68);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    text-align: center;
    padding: 5px 8px;
  }

  /* Image zone — FIXED height so every card is identical */
  .sb-coffee-img {
    width: 100%;
    height: 230px;
    background: #fff;
    position: relative;
    flex-shrink: 0;
    overflow: hidden;
  }
  .sb-coffee-card.is-selected .sb-coffee-img { background: #fff; }
  .sb-coffee-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 10px;
    box-sizing: border-box;
    display: block;
    transition: transform 0.3s ease;
  }
  .sb-coffee-card:hover .sb-coffee-img img { transform: scale(1.06); }

  /* Selected checkmark badge */
  .sb-coffee-check {
    position: absolute;
    top: 6px;
    right: 6px;
    background: #9f6a33;
    color: white;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-size: 0.72em;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.18);
  }

  /* Quantity badge — shows how many bags of this coffee are chosen */
  .sb-coffee-qty-badge {
    position: absolute;
    top: 6px;
    right: 6px;
    background: #9f6a33;
    color: white;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }

  /* Per-card quantity stepper */
  .sb-coffee-stepper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 4px;
  }

  /* ── Per-roast steppers on coffee cards ── */
  .sb-coffee-roasts {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
    width: 100%;
    border-top: 1px solid #ede6db;
    padding-top: 8px;
  }
  .sb-stepper-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 4px;
  }
  .sb-roast-label {
    font-size: 0.75em;
    font-weight: 700;
    color: #7a5c35;
    flex: 1;
    text-align: left;
  }
  .sb-roast-stepper {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ── Curated roast picker ── */
  .sb-curated-roast-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
    width: 100%;
    justify-content: center;
  }
  .sb-curated-roast-label {
    font-size: 0.78em;
    font-weight: 600;
    color: #6b4f35;
    width: 100%;
    text-align: center;
    margin-bottom: 2px;
  }
  .sb-roast-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    border: 1.5px solid #c4a07a;
    background: #fff;
    color: #6b4f35;
    font-size: 0.78em;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .sb-roast-chip.is-active {
    background: #9f6a33;
    border-color: #9f6a33;
    color: #fff;
  }
  .sb-curated-roast-hint {
    font-size: 0.75em;
    color: #c05c1a;
    margin: 4px 0 0;
    font-weight: 500;
  }
  .sb-curated-remove {
    display: inline-block;
    margin-top: 8px;
    background: none;
    border: none;
    color: #999;
    font-size: 0.75em;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }
  .sb-curated-remove:hover { color: #c0392b; }

  /* ── Step 2: selection progress bar ── */
  .sb-progress {
    margin-bottom: 16px;
    padding: 12px 14px;
    background: #faf6f1;
    border: 1px solid #ece2d6;
    border-radius: 10px;
  }
  .sb-progress-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }
  .sb-progress-title {
    font-size: 0.78em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #3b2314;
  }
  .sb-progress-count {
    font-size: 0.82em;
    font-weight: 700;
    color: #9f6a33;
    white-space: nowrap;
  }
  .sb-progress-count.is-complete { color: #5a9a5a; }
  .sb-progress-track {
    height: 8px;
    border-radius: 5px;
    background: #ece2d6;
    overflow: hidden;
  }
  .sb-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #c2976a 0%, #9f6a33 100%);
    border-radius: 5px;
    transition: width 0.35s cubic-bezier(0.2, 0.8, 0.3, 1);
  }
  .sb-progress-hint {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 9px 0 0;
    font-size: 0.8em;
    font-weight: 600;
    color: #9f6a33;
  }
  .sb-progress-hint .sb-step-guide-arrow { background: #9f6a33; color: #fff; }
  .sb-progress-hint--done { color: #5a9a5a; }

  /* Info zone */
  .sb-coffee-info {
    padding: 9px 10px 11px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex: 1;
    border-top: 1px solid #f0ebe3;
    width: 100%;
    box-sizing: border-box;
  }
  .sb-coffee-card.is-selected .sb-coffee-info { border-top-color: #edd9b8; }
  .sb-coffee-name {
    font-size: 0.84em;
    font-weight: 800;
    color: #3b2314;
    line-height: 1.3;
  }
  .sb-coffee-tag-row { display: flex; flex-wrap: wrap; gap: 3px; justify-content: center; }
  .sb-coffee-tag {
    font-size: 0.64em;
    font-weight: 600;
    text-transform: capitalize;
    background: #f0e8dc;
    color: #7a5c35;
    padding: 2px 6px;
    border-radius: 8px;
    white-space: nowrap;
  }
  .sb-coffee-card.is-selected .sb-coffee-tag { background: #f5e5c8; color: #7a3e0a; }
  .sb-coffee-tag--flavor, .sb-coffee-tag--processing, .sb-coffee-tag--note { /* inherits .sb-coffee-tag */ }
  .sb-no-results { font-size: 0.85em; color: #7a6a5a; grid-column: 1 / -1; padding: 12px 0; }

  /* ── Step 3: Summary bar ── */
  .sb-summary-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 9px 12px;
    background: #f9f4ef;
    border-radius: 7px;
    margin-bottom: 16px;
    font-size: 0.82em;
  }
  .sb-summary-pill { color: #555; font-weight: 500; }
  .sb-summary-pill--coffee { color: #3b2314; font-weight: 700; }
  .sb-summary-sep { color: #ccc; }
  .sb-summary-price { margin-left: auto; font-weight: 700; color: #3b2314; white-space: nowrap; }

  /* Addon intro text */
  .sb-addon-intro {
    font-size: 0.85em;
    color: #7a6a5a;
    margin: 0 0 12px;
  }

  /* ── Addon grid — same card design as coffee grid ── */
  .sb-carousel {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }
  /* Roast is picked from the steppers inside the card, so the card itself isn't clickable */
  .sb-carousel-card {
    border: 1.5px solid #dccfbf;
    border-radius: 12px;
    overflow: hidden;
    background: white;
    text-align: center;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 6px rgba(59,35,20,0.07);
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.18s;
  }
  .sb-carousel-card .sb-coffee-info { border-top: 1px solid #f0ebe3; }
  .sb-carousel-card.is-selected .sb-coffee-info { border-top-color: #edd9b8; }
  .sb-carousel-card:hover {
    border-color: #c2976a;
    box-shadow: 0 8px 22px rgba(194,151,106,0.2);
    transform: translateY(-3px);
  }
  .sb-carousel-card.is-selected {
    border: 2px solid #9f6a33;
    box-shadow: 0 8px 22px rgba(159,106,51,0.24);
    transform: translateY(-3px);
  }
  .sb-carousel-img {
    width: 100%;
    height: 230px;
    background: #fff;
    position: relative;
    flex-shrink: 0;
    overflow: hidden;
  }
  .sb-carousel-card.is-selected .sb-carousel-img { background: #fff; }
  .sb-carousel-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 10px;
    box-sizing: border-box;
    display: block;
    transition: transform 0.3s ease;
  }
  .sb-carousel-card:hover .sb-carousel-img img { transform: scale(1.06); }

  /* Selected add-ons list — one row per chosen add-on */
  .sb-addon-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }
  .sb-addon-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 14px;
    background: #f9f4ef;
    border: 1px solid #ece2d6;
    border-radius: 8px;
    padding: 10px 12px;
  }
  .sb-addon-row-name {
    font-weight: 700;
    color: #3b2314;
    font-size: 0.9em;
    flex: 1 1 140px;
    min-width: 0;
  }
  /* Read-only roast tag — deliberately not chip-styled, so it doesn't read as clickable */
  .sb-addon-row-roast {
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #7a5c35;
    background: #f0e8dc;
    border-radius: 20px;
    padding: 3px 10px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .sb-addon-row-price {
    font-weight: 700;
    color: #3b2314;
    font-size: 0.88em;
    margin-left: auto;
    white-space: nowrap;
  }
  .sb-addon-remove {
    border: none;
    background: none;
    color: #b07c2c;
    cursor: pointer;
    font-size: 0.9em;
    padding: 4px 6px;
    border-radius: 6px;
    line-height: 1;
    transition: background 0.15s, color 0.15s;
  }
  .sb-addon-remove:hover { background: rgba(190,30,45,0.1); color: #be1e2d; }
  .sb-qty-row { display: flex; align-items: center; gap: 7px; }
  .sb-qty-btn {
    width: 28px;
    height: 28px;
    border: 1.5px solid #ddd;
    border-radius: 5px;
    background: white;
    cursor: pointer;
    font-size: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b2314;
    transition: border-color 0.15s;
  }
  .sb-qty-btn:hover    { border-color: #c2976a; }
  .sb-qty-btn:disabled { opacity: 0.35; cursor: default; }
  .sb-qty-val { font-weight: 700; min-width: 22px; text-align: center; font-size: 0.95em; }
  .sb-onetime-badge {
    background: #e0d6cc;
    color: #777;
    font-size: 0.82em;
    padding: 1px 6px;
    border-radius: 8px;
  }

  /* ── Order Summary section ── */
  .sb-order-summary {
    border-top: 1px solid #f0ebe5;
    padding-top: 18px;
    margin-top: 16px;
  }
  .sb-order-summary-title {
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #3b2314;
    margin-bottom: 14px;
  }
  .sb-extras {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 14px;
  }
  .sb-extra-label { display: flex; flex-direction: column; gap: 5px; }
  .sb-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-top: 1px solid #f5f1ed;
    font-size: 0.85em;
  }
  .sb-total-label { color: #7a6a5a; }
  .sb-total-val   { font-weight: 600; color: #3b2314; }
  .sb-total-free  { color: #5a9a5a; }
  .sb-total-muted { color: #9c8878; font-weight: 500; font-size: 0.9em; font-style: italic; }
  .sb-total-row--grand {
    border-top: 2px solid #e3d8cc;
    margin-top: 4px;
    padding-top: 10px;
  }
  .sb-grand-label {
    font-weight: 700;
    color: #3b2314;
    font-size: 1em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .sb-grand-val {
    font-size: 1.15em;
    font-weight: 800;
    color: #3b2314;
  }

  /* ── Optional badge / curated note / error ── */
  .sb-optional-tag {
    font-size: 0.62em;
    background: #f0ebe5;
    color: #7a6a5a;
    padding: 3px 8px;
    border-radius: 9px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 400;
    vertical-align: middle;
  }
  .sb-curated-note {
    font-size: 0.82em;
    color: #be1e2d;
    background: #fef5f5;
    border: 1px solid #f5c0c0;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 12px;
  }
  .sb-error { font-size: 0.85em; color: #be1e2d; margin-bottom: 10px; }

  /* ── Nav ── */
  .sb-nav {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid #f0ebe5;
    margin-top: 8px;
  }
  .sb-nav--split { justify-content: space-between; }
  .sb-btn-primary {
    background: #3b2314;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 10px 22px;
    font-size: 0.9em;
    cursor: pointer;
    font-weight: 600;
    transition: opacity 0.15s;
  }
  .sb-btn-primary:hover    { opacity: 0.88; }
  .sb-btn-primary:disabled { background: #ccc; cursor: default; opacity: 1; }
  .sb-btn-secondary {
    background: none;
    border: 1.5px solid #c8b8a8;
    color: #7a6a5a;
    border-radius: 5px;
    padding: 9px 16px;
    font-size: 0.88em;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .sb-btn-secondary:hover { border-color: #3b2314; color: #3b2314; }
  .sb-btn-cta {
    background: #9f6a33;
    color: white;
    border: none;
    border-radius: 3px;
    padding: 0.075em 1.5em 0;
    height: 48px;
    line-height: 46px;
    font-family: "din-condensed-web", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 1em;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
    white-space: nowrap;
    min-width: 160px;
  }
  .sb-btn-cta:hover    { background: #3b2314; }
  .sb-btn-cta:disabled { background: #ccc; cursor: default; }

  /* ── Sticky nav (step 2) ── */
  .sb-nav--sticky {
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 2px solid #e8ddd2;
    margin-left: -28px;
    margin-right: -28px;
    margin-bottom: -20px;
    padding: 12px 28px;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -4px 18px rgba(59,35,20,0.1);
    z-index: 20;
  }

  /* Selection badge */
  .sb-selection-badge {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 14px;
    background: #f9f4ef;
    border: 1.5px solid #c2976a;
    border-radius: 8px;
    animation: sb-guide-in 0.3s ease;
  }
  .sb-selection-badge.is-complete { border-color: #5a9a5a; background: #f1f8f1; }
  .sb-selection-badge.is-complete .sb-sel-check { background: #5a9a5a; }
  .sb-selection-badge.is-complete .sb-sel-count { color: #4a8a4a; }
  .sb-sel-check {
    width: 22px;
    height: 22px;
    background: #9f6a33;
    color: white;
    border-radius: 50%;
    font-size: 0.72em;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sb-sel-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .sb-sel-count {
    font-size: 0.68em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #9f6a33;
  }
  .sb-sel-name {
    font-size: 0.8em;
    font-weight: 600;
    color: #3b2314;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Step guidance hints ── */
  .sb-step-guide {
    display: flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    max-width: 100%;
    margin: 2px auto 12px;
    font-size: 0.82em;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, #b07c2c 0%, #9f6a33 100%);
    border: none;
    border-radius: 24px;
    padding: 8px 18px;
    text-align: center;
    box-shadow: 0 4px 14px rgba(159,106,51,0.28);
    animation: sb-guide-pop 0.4s cubic-bezier(0.2, 0.8, 0.3, 1.25),
               sb-guide-glow 1.8s ease-in-out 0.4s infinite;
  }
  .sb-step-guide-arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 50%;
    background: rgba(255,255,255,0.22);
    font-size: 1.05em;
    font-weight: 900;
    line-height: 1;
    animation: sb-arrow-bounce 0.9s ease-in-out infinite;
  }
  @keyframes sb-arrow-bounce {
    0%, 100% { transform: translateY(-2px); opacity: 0.85; }
    50%       { transform: translateY(3px);  opacity: 1; }
  }

  /* Highlight the actual target area (frequency buttons) as the next step */
  .sb-freq-col.is-highlight {
    border-radius: 9px;
    animation: sb-target-pulse 1.8s ease-in-out infinite;
  }
  @keyframes sb-target-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(159,106,51,0); }
    50%       { box-shadow: 0 0 0 5px rgba(159,106,51,0.16); }
  }
  @keyframes sb-guide-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes sb-guide-pop {
    0%   { opacity: 0; transform: scale(0.85) translateY(-8px); }
    60%  { opacity: 1; transform: scale(1.03) translateY(0); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes sb-guide-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(159,106,51,0); }
    50%       { box-shadow: 0 0 0 6px rgba(159,106,51,0.14); }
  }

  /* Locked coffee grid placeholder (before a roast is picked) */
  .sb-coffee-locked {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 48px 20px;
    border: 2px dashed #e0d3c2;
    border-radius: 12px;
    background: #faf6f1;
    color: #9c8878;
    margin-bottom: 16px;
  }
  .sb-coffee-locked-icon { font-size: 1.8em; opacity: 0.7; }
  .sb-coffee-locked p { margin: 0; font-size: 0.9em; font-weight: 600; }

  /* Attention pulse on frequency section label */
  @keyframes sb-label-glow {
    0%, 100% { color: #7a6a5a; }
    50%       { color: #9f6a33; }
  }
  .sb-field-label.needs-attention,
  .sb-filter-group-label.needs-attention {
    animation: sb-label-glow 1.6s ease-in-out infinite;
  }

  /* Pulse on next-step button when step is complete */
  @keyframes sb-btn-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59,35,20,0.28); }
    50%       { box-shadow: 0 0 0 7px rgba(59,35,20,0); }
  }
  .sb-btn-primary.is-ready-pulse:not(:disabled) {
    animation: sb-btn-pulse 2s ease-in-out infinite;
  }
  .sb-btn-cta.is-ready-pulse:not(:disabled) {
    animation: sb-btn-pulse 2s ease-in-out infinite;
  }

  /* ── Responsive ── */

  /* Collapse sidebar to horizontal steps on tablet/mobile */
  @media (max-width: 720px) {
    .sb {
      grid-template-columns: 1fr;
      padding: 0 16px 48px;
      gap: 0;
    }
    .sb-steps {
      flex-direction: row;
      align-items: center;
      justify-content: center;
      padding-top: 4px;
      padding-right: 0;
      border-right: none;
      position: static;
      margin-bottom: 16px;
    }
    .sb-step {
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      width: auto;
    }
    .sb-step-circle { width: 38px; height: 38px; font-size: 0.9em; }
    .sb-step-label  { font-size: 0.7em; }
    .sb-step-line {
      flex: 1;
      height: 2px;
      width: auto;
      margin: 0 0 22px 0;
      min-width: 24px;
      max-width: 80px;
    }
  }

  @media (max-width: 1024px) {
    .sb-coffee-grid, .sb-carousel { grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .sb-coffee-img, .sb-carousel-img { height: 200px; }
  }
  @media (max-width: 760px) {
    .sb-coffee-grid, .sb-carousel { gap: 10px; }
    .sb-coffee-img, .sb-carousel-img { height: 170px; }
  }
  @media (max-width: 600px) {
    .sb-panel { padding: 16px 14px; }
    .sb-nav--sticky {
      margin-left: -14px;
      margin-right: -14px;
      margin-bottom: -16px;
      padding: 12px 14px;
    }
    /* keep 2 cards per row on phones */
    .sb-coffee-grid, .sb-carousel { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    .sb-coffee-img, .sb-carousel-img { height: 150px; }
    .sb-filter-row { flex-wrap: wrap; }
    .sb-sel-name { max-width: 120px; }
  }

</style>
